import { forwardRef, type ReactNode, type InputHTMLAttributes } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helperText?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, leadingIcon, trailingIcon, className = '', id, ...props }, ref) => {
    const uid = id || (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={uid} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
          </label>
        )}
        <div className="relative">
          {leadingIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center">
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            id={uid}
            className={`
              w-full h-10 px-3 text-sm rounded-lg border bg-white dark:bg-gray-900
              text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500
              transition-colors duration-150
              border-gray-200 dark:border-gray-700
              hover:border-gray-300 dark:hover:border-gray-600
              focus:outline-none focus:ring-2 focus:ring-slate-900/20 dark:focus:ring-white/20 focus:border-slate-400 dark:focus:border-gray-500
              disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800
              ${leadingIcon ? 'pl-10' : ''}
              ${trailingIcon ? 'pr-10' : ''}
              ${error ? 'border-red-400 dark:border-red-500 focus:ring-red-500/20' : ''}
              ${className}
            `}
            {...props}
          />
          {trailingIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center">
              {trailingIcon}
            </span>
          )}
        </div>
        {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}
        {helperText && !error && <p className="text-xs text-gray-400 dark:text-gray-500">{helperText}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';
